import { Option, Select, TextInput } from '@d-edge/overnight-hotelier-react'
import { Button, IconButton } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Icons } from '@/components/ui/Icons'
import { NumberInput } from './NumberInput'
import { actionFor, actionMode, type Operator, type RuleDraft, type ScaleAction } from './scalability'
import styles from './ScalabilityDialog.module.css'

interface MetricFieldsProps {
  rule: RuleDraft
  /** 1-based position, used to give every control an unambiguous accessible name. */
  position: number
  onChange: (patch: Partial<RuleDraft>) => void
}

export function MetricFields({ rule, position, onChange }: MetricFieldsProps) {
  const id = `rule-${rule.key}`
  const scalingOut = rule.operator === '>='
  // The stored action depends on the operator, so it is recomputed whenever the operator changes.
  const changeOperator = (operator: Operator) => onChange({ operator, action: actionFor(operator, actionMode(rule.action)) })
  const setQueue = (index: number, value: string) => onChange({ queues: rule.queues.map((queue, i) => (i === index ? value : queue)) })

  return (
    <>
      <div className={styles.fieldGrid}>
        <Field label="Direction">
          <Select fullWidth value={rule.operator} onChange={(value) => changeOperator(value as Operator)}>
            <Option value=">=" label="Scale out" />
            <Option value="<=" label="Scale in" />
          </Select>
        </Field>
        <Field label="Action">
          <Select fullWidth value={rule.action} onChange={(value) => onChange({ action: value as ScaleAction })}>
            <Option value={actionFor(rule.operator, 'by')} label="by" />
            <Option value={actionFor(rule.operator, 'to')} label="to" />
          </Select>
        </Field>
        <Field label="Instances" htmlFor={`${id}-instances`}>
          <NumberInput id={`${id}-instances`} min={1} value={rule.instances} onChange={(instances) => onChange({ instances })} />
        </Field>
      </div>

      <div className={styles.sentence}>
        <span>ApproximateMessageCount {scalingOut ? '≥' : '≤'}</span>
        <NumberInput
          min={0}
          value={rule.messageCountPerMachine}
          aria-label={`Message count per machine of rule ${position}`}
          fullWidth={false}
          className={styles.sentenceInput}
          onChange={(messageCountPerMachine) => onChange({ messageCountPerMachine })}
        />
        <span>per machine</span>
      </div>

      <div className={styles.queues} role="group" aria-label={`Queues of rule ${position}`}>
        {rule.queues.map((queue, index) => (
          <div key={index} className={styles.queueRow}>
            <span className={styles.prefix}>{index === 0 ? 'When' : scalingOut ? 'Or' : 'And'}</span>
            <TextInput fullWidth value={queue} placeholder="Queue name" aria-label={`Queue ${index + 1} of rule ${position}`} onChange={(e) => setQueue(index, e.target.value)} />
            <IconButton
              label={`Remove queue ${index + 1} of rule ${position}`}
              disabled={rule.queues.length === 1}
              onClick={() => onChange({ queues: rule.queues.filter((_, i) => i !== index) })}
            >
              <Icons.Close size={13} />
            </IconButton>
          </div>
        ))}
        <div>
          <Button size="sm" variant="ghost" icon={<Icons.Plus size={12} />} onClick={() => onChange({ queues: [...rule.queues, ''] })}>
            Add a queue
          </Button>
        </div>
      </div>
    </>
  )
}
